import React, { useState, useEffect } from 'react';

interface TimeLeft {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}

const HackathonTimeline: React.FC = () => {
  const [timeLeft, setTimeLeft] = useState<TimeLeft>({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
  });

  useEffect(() => {
    const targetDate = new Date('2025-06-18T23:59:59Z');

    const calculateTimeLeft = () => {
      const now = new Date();
      const difference = targetDate.getTime() - now.getTime();

      if (difference > 0) {
        setTimeLeft({
          days: Math.floor(difference / (1000 * 60 * 60 * 24)),
          hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
          minutes: Math.floor((difference / 1000 / 60) % 60),
          seconds: Math.floor((difference / 1000) % 60),
        });
      }
    };

    calculateTimeLeft();
    const timer = setInterval(calculateTimeLeft, 1000);

    return () => clearInterval(timer);
  }, []);

  const milestones = [
    {
      date: 'June 1',
      title: 'Hackathon Begins',
      subtitle: 'Hybrid, Worldwide',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6">
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      color: 'text-cyan-500',
      borderColor: 'border-cyan-500',
      hoverBg: 'hover:bg-cyan-50',
    },
    {
      date: 'June 18',
      title: 'Submissions Close',
      subtitle: 'Final Deadline',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6">
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      color: 'text-yellow-500',
      borderColor: 'border-yellow-500',
      hoverBg: 'hover:bg-yellow-50',
    },
    {
      date: 'June 20',
      title: 'Judging Begins',
      subtitle: 'Expert Review',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6">
          <path strokeLinecap="round" strokeLinejoin="round" d="M9.568 3H5.25A2.25 2.25 0 003 5.25v4.318c0 .597.237 1.17.659 1.591l9.581 9.581c.699.699 1.78.872 2.607.33a18.095 18.095 0 005.223-5.223c.542-.827.369-1.908-.33-2.607L11.16 3.66A2.25 2.25 0 009.568 3z" />
          <path strokeLinecap="round" strokeLinejoin="round" d="M6 6h.008v.008H6V6z" />
        </svg>
      ),
      color: 'text-purple-500',
      borderColor: 'border-purple-500',
      hoverBg: 'hover:bg-purple-50',
    },
    {
      date: 'June 21',
      title: 'Finalists Announced',
      subtitle: 'Top 10 Projects',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6">
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      color: 'text-green-500',
      borderColor: 'border-green-500',
      hoverBg: 'hover:bg-green-50',
    },
    {
      date: 'June 22',
      title: 'Winners Announced',
      subtitle: 'Awards Ceremony',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6">
          <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 18.75h-9m9 0a3 3 0 013 3h-15a3 3 0 013-3m9 0v-3.375c0-.621-.503-1.125-1.125-1.125h-.871M7.5 18.75v-3.375c0-.621.504-1.125 1.125-1.125h.872m5.007 0H9.497m5.007 0a7.454 7.454 0 01-.982-3.172M9.497 14.25a7.454 7.454 0 00.981-3.172M5.25 4.236c-.982.143-1.954.317-2.916.52A6.003 6.003 0 007.73 9.728M5.25 4.236V4.5c0 2.108.966 3.99 2.48 5.228M5.25 4.236V2.721C7.456 2.41 9.71 2.25 12 2.25c2.291 0 4.545.16 6.75.47v1.516M7.73 9.728a6.726 6.726 0 002.748 1.35m8.272-6.842V4.5c0 2.108-.966 3.99-2.48 5.228m2.48-5.492a46.32 46.32 0 012.916.52 6.003 6.003 0 01-5.395 4.972m0 0a6.726 6.726 0 01-2.749 1.35m0 0a6.772 6.772 0 01-3.044 0" />
        </svg>
      ),
      color: 'text-red-500',
      borderColor: 'border-red-500',
      hoverBg: 'hover:bg-red-50',
    },
  ];

  return (
    <div className="w-full max-w-7xl mx-auto px-4 py-12 space-y-16">
      <div className="relative max-w-7xl w-full mx-auto">
        {/* Semi-transparent overlay */}
        <div className="absolute inset-0 bg-white/80 backdrop-blur-sm rounded-2xl" />
        
        <div className="relative z-10 space-y-16 p-4 sm:p-8 md:p-12">
          {/* Countdown Clock */}
          <div className="text-center space-y-8">
            <h2 className="text-lg font-medium tracking-wide text-slate-600 uppercase">
              Time Left to Submit
            </h2>
            <div className="grid grid-cols-2 sm:flex sm:justify-center gap-4 sm:gap-8 md:gap-12">
              {Object.entries(timeLeft).map(([unit, value]) => (
                <div key={unit} className="text-center">
                  <div className="text-5xl sm:text-6xl md:text-7xl font-bold text-slate-900 bg-white/50 rounded-xl p-4 sm:p-6 shadow-sm">
                    {value}
                  </div>
                  <div className="text-sm sm:text-base md:text-lg text-slate-500 uppercase mt-2 sm:mt-3 font-medium tracking-wide">{unit}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Timeline Milestones */}
          <div className="relative">
            <div className="flex flex-col md:flex-row justify-center items-center gap-8">
              {milestones.map((milestone, index) => (
                <div key={index} className="w-full md:flex-1 max-w-[250px] group">
                  {/* Content */}
                  <div className={`flex flex-col items-center bg-white/50 p-6 border-2 ${milestone.borderColor} ${milestone.hoverBg}
                    transition-all duration-300 ease-in-out
                    hover:shadow-lg hover:-translate-y-2 hover:rotate-2
                    group-hover:border-opacity-100 border-opacity-50`}>
                    {/* Icon and Date */}
                    <div className="flex flex-col items-center gap-3 mb-4">
                      <div className={`${milestone.color} transition-transform duration-300 group-hover:scale-110 group-hover:rotate-12`}>
                        {milestone.icon}
                      </div>
                      <div className="text-lg font-bold text-slate-900">
                        {milestone.date}
                      </div>
                    </div>

                    {/* Text Content */}
                    <div className="space-y-1 text-center">
                      <div className="text-base font-medium text-slate-700">{milestone.title}</div>
                      <div className="text-sm text-slate-500">{milestone.subtitle}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HackathonTimeline; 